// src/components/layout/Layout.jsx
//
// App shell — 232px black sidebar in flow, main column scrolls inside the content frame.
// The main column has: TopBar (search + bell + optional CTA), an optional
// PageHeader (eyebrow + serif H1 + optional CTA), then the page body.
//
// Pages that want the new page header pass `eyebrow`, `title`, and/or
// `rightSlot`. Pages that don't will fall back to the breadcrumbs banner.
//
// Props:
//   - eyebrow:        string above the page title
//   - title:          string | node — page H1
//   - rightSlot:      node — page-level CTA, aligned right of the H1
//   - topBarRight:    node — optional CTA in the top bar (mirrors the page CTA)
//   - hidePageHeader: bool — force-hide the page header even if title is set
//   - hideBreadcrumbs: bool
import React, { useEffect } from "react";
import { Box } from "@mui/material";
import Navbar from "./Navbar";
import Breadcrumbs from "./Breadcrumbs";
import TopBar from "./TopBar";
import PageHeader from "./PageHeader";
import Footer from "./Footer";
import FeedbackCollector from "../../feedback/FeedbackCollector";
import { APP_ENVIRONMENT } from "../../config";
import { layout } from "../../theme/tokens";
import { APP_SHELL_MAIN_ID } from "./shellFrame";

const SIDEBAR_WIDTH = layout.sidebarWidth;

export default function Layout({
  children,
  eyebrow,
  title,
  rightSlot,
  topBarRight,
  hidePageHeader = false,
  hideBreadcrumbs = false,
}) {
  const showPageHeader = !hidePageHeader && (eyebrow || title);
  const watermarkLabel = (APP_ENVIRONMENT || "").toUpperCase();
  const showEnvironmentWatermark =
    watermarkLabel === "UAT" || watermarkLabel === "DEV";

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);

  return (
    <FeedbackCollector>
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        overflow: "hidden",
        backgroundColor: "background.default",
      }}
    >
      <Box sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, height: "100%", minHeight: 0 }}>
        <Navbar />
      </Box>

      <Box
        id={APP_SHELL_MAIN_ID}
        sx={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
          "& > .MuiDialog-root": {
            position: "absolute",
            inset: 0,
          },
        }}
      >
        {showEnvironmentWatermark && (
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              userSelect: "none",
              zIndex: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                fontSize: { xs: "13.5rem", md: "24rem", lg: "33rem" },
                fontWeight: 900,
                letterSpacing: "0.18em",
                lineHeight: 1,
                color: "rgba(201,161,75,0.10)",
                transform: "rotate(-32deg)",
                whiteSpace: "nowrap",
                textTransform: "uppercase",
              }}
            >
              {watermarkLabel}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TopBar rightSlot={topBarRight} />

          {showPageHeader && (
            <PageHeader eyebrow={eyebrow} title={title} rightSlot={rightSlot} />
          )}

          <Box
            sx={{
              flex: 1,
              px: { xs: 2.5, md: 3.5 },
              py: 3.5,
              backgroundColor: "background.default",
            }}
          >
            {!hideBreadcrumbs && !showPageHeader && <Breadcrumbs />}
            {children}
          </Box>

          <Footer />
        </Box>
      </Box>
    </Box>
    </FeedbackCollector>
  );
}
